import { __assign, __rest } from 'tslib';
import { createRef, useEffect } from 'react';
import { useComposedRefs } from '../../hooks/useComposedRefs.js';
import '../../hooks/useDetectInputMethod.js';
import '@reach/auto-id';
import '@emotion/react';
import 'lodash.get';
import '@compono/ui-theme';
import 'deepmerge';
import '@emotion/is-prop-valid';
import { useDatePickerRangeContext } from '../../DatePickerRange/DatePickerRangeContext.js';
import parseISO from '../../node_modules/date-fns/esm/parseISO/index.js';
import isSameDay from '../../node_modules/date-fns/esm/isSameDay/index.js';
import format from '../../node_modules/date-fns/esm/format/index.js';
import parse from '../../node_modules/date-fns/esm/parse/index.js';
import formatISO from '../../node_modules/date-fns/esm/formatISO/index.js';

/** @jsxImportSource @emotion/react */
var defaultDateFormat = "d/M/y";
var validateDate = function (date) { return !isNaN(Number(date)); };
var convertDateToInput = function (date, dateFormat) {
    if (dateFormat === void 0) { dateFormat = defaultDateFormat; }
    return date ? format(date, dateFormat) : "";
};
var convertDateToIso = function (date) {
    return date ? formatISO(date, { representation: "date" }) : "Invalid Date";
};
var convertInputToDate = function (value, dateFormat, dateRef) {
    if (dateFormat === void 0) { dateFormat = defaultDateFormat; }
    if (dateRef === void 0) { dateRef = new Date(0, 0, 0, 0, 0, 0); }
    return parse(value, dateFormat, dateRef);
};
var convertIsoToDate = function (value) {
    return parseISO(value);
};
var useIsStart = function (_a) {
    var type = _a.type;
    return type === "start";
};
var useRangeInputContext = function (props) {
    return function (context) {
        var startInputRef = context.startInputRef, endInputRef = context.endInputRef, setStartInputValue = context.setStartInputValue, setEndInputValue = context.setEndInputValue, setStartDateValue = context.setStartDateValue, setStartInputProps = context.setStartInputProps, setEndDateValue = context.setEndDateValue, setEndInputProps = context.setEndInputProps, startDateValue = context.startDateValue, endDateValue = context.endDateValue, startInputValue = context.startInputValue, endInputValue = context.endInputValue;
        var isStart = useIsStart(props);
        return isStart
            ? {
                inputRef: startInputRef,
                setInputValue: setStartInputValue,
                setDateValue: setStartDateValue,
                setInputProps: setStartInputProps,
                dateValue: startDateValue,
                inputValue: startInputValue,
            }
            : {
                inputRef: endInputRef,
                setInputValue: setEndInputValue,
                setDateValue: setEndDateValue,
                setInputProps: setEndInputProps,
                dateValue: endDateValue,
                inputValue: endInputValue,
            };
    };
};
var useInputKeyDownHandler = function (props) {
    return function (context) {
        return function (event) {
            var onKeyDown = props.onKeyDown;
            var setOpenedBy = context.setOpenedBy, isOpen = context.isOpen, setIsOpen = context.setIsOpen, focusDateRef = context.focusDateRef;
            onKeyDown && onKeyDown(event);
            if (event.isDefaultPrevented())
                return;
            switch (event.code) {
                case "ArrowDown":
                    openDatePicker(props)(context);
                    setTimeout(function () {
                        var _a;
                        (_a = focusDateRef === null || focusDateRef === void 0 ? void 0 : focusDateRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    });
                    break;
                case "Escape":
                    if (isOpen) {
                        setIsOpen(false);
                        setOpenedBy(undefined);
                    }
                    break;
            }
        };
    };
};
var useInputClickHandler = function (props) {
    return function (context) {
        return function (event) {
            var _a;
            var onClick = props.onClick;
            var inputRef = useRangeInputContext(props)(context).inputRef;
            onClick && onClick(event);
            if (event.isDefaultPrevented())
                return;
            if (!event.isDefaultPrevented()) {
                openDatePicker(props)(context);
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
    };
};
var useInputChangeBlurHandler = function (props) {
    return function (context) {
        return function (type) {
            return function (event) {
                var onChange = props.onChange, onBlur = props.onBlur;
                var inputFormat = context.inputFormat;
                var _a = useRangeInputContext(props)(context), setInputValue = _a.setInputValue, setDateValue = _a.setDateValue, dateValue = _a.dateValue;
                var inputValue = event.target.value.trim();
                setInputValue(inputValue);
                var date = convertInputToDate(inputValue, inputFormat, dateValue);
                var isDateValid = validateDate(date);
                if (isDateValid && dateValue && isSameDay(date, dateValue))
                    return;
                setDateValue(isDateValid ? date : undefined);
                var value = isDateValid ? convertDateToIso(date) : "Invalid Date";
                var outputValue = inputValue === "" ? "" : value;
                type === "change" && onChange && onChange(outputValue);
                type === "blur" && onBlur && onBlur(outputValue);
            };
        };
    };
};
var useInputFocusHandler = function (props) {
    return function (context) {
        return function () {
            var type = props.type;
            var isOpen = context.isOpen, setOpenedBy = context.setOpenedBy;
            isOpen && setOpenedBy(type);
        };
    };
};
var openDatePicker = function (_a) {
    var disabled = _a.disabled, readOnly = _a.readOnly, type = _a.type;
    return function (_a) {
        var setOpenedBy = _a.setOpenedBy, isOpen = _a.isOpen, setIsOpen = _a.setIsOpen;
        if (disabled || readOnly)
            return;
        setOpenedBy(type);
        !isOpen && setIsOpen(true);
    };
};
var useCombinedRef = function (props) {
    return function (context) {
        var _a = props.ref, ref = _a === void 0 ? createRef() : _a;
        var startInputRef = context.startInputRef, triggerRef = context.triggerRef;
        var isStart = useIsStart(props);
        var inputRef = useRangeInputContext(props)(context).inputRef;
        return useComposedRefs(ref, isStart
            ? function (element) {
                startInputRef.current = element;
                triggerRef.current = element;
            }
            : inputRef);
    };
};
var useRangeInputEffect = function (props) {
    return function (context) {
        var onChange = props.onChange, value = props.value;
        var inputFormat = context.inputFormat, endDateValue = context.endDateValue;
        var _a = useRangeInputContext(props)(context), setInputValue = _a.setInputValue, setDateValue = _a.setDateValue, setInputProps = _a.setInputProps;
        useEffect(function () {
            setInputProps({ onChange: onChange });
        }, [onChange]);
        useEffect(function () {
            if (!value) {
                setInputValue("");
                setDateValue(undefined);
                return;
            }
            var date = convertIsoToDate(value || "");
            if (endDateValue && isSameDay(date, endDateValue))
                return;
            if (validateDate(date)) {
                setInputValue(convertDateToInput(date, inputFormat));
                setDateValue(date);
            }
        }, [value]);
    };
};
var useDatePickerRangeInput = function (propsArg) {
    propsArg.onBlur; propsArg.onChange; propsArg.onClick; propsArg.onKeyDown; propsArg.value; propsArg.defaultValue; var idArg = propsArg.id, sizeArg = propsArg.size, type = propsArg.type, disabled = propsArg.disabled, readOnly = propsArg.readOnly, _a = propsArg.placeholder, placeholder = _a === void 0 ? "Select a date" : _a, props = __rest(propsArg, ["onBlur", "onChange", "onClick", "onKeyDown", "value", "defaultValue", "id", "size", "type", "disabled", "readOnly", "placeholder"]);
    var context = useDatePickerRangeContext();
    var size = context.size, anchorRef = context.anchorRef, openedBy = context.openedBy, isOpen = context.isOpen, id = context.id;
    var isStart = useIsStart(propsArg);
    var inputValue = useRangeInputContext(propsArg)(context).inputValue;
    var combinedRef = useCombinedRef(propsArg)(context);
    var inputKeyDownHandler = useInputKeyDownHandler(propsArg)(context);
    var inputClickHandler = useInputClickHandler(propsArg)(context);
    var inputChangeBlurHandler = useInputChangeBlurHandler(propsArg)(context);
    var inputChangeHandler = inputChangeBlurHandler("change");
    var inputBlurHandler = inputChangeBlurHandler("blur");
    var inputFocusHandler = useInputFocusHandler(propsArg)(context);
    var myId = idArg ? idArg : "range-".concat(type, "--").concat(id);
    useRangeInputEffect(propsArg)(context);
    return __assign(__assign(__assign(__assign({}, props), { id: myId, size: sizeArg ? sizeArg : size, onKeyDown: inputKeyDownHandler, onClick: inputClickHandler, onChange: inputChangeHandler, onBlur: inputBlurHandler, onFocus: inputFocusHandler, value: inputValue, placeholder: placeholder, "aria-controls": "".concat(id, "-dropdown"), autoComplete: "off", ref: combinedRef, disabled: disabled, readOnly: readOnly }), (isStart && {
        containerRef: anchorRef,
    })), (openedBy === type &&
        isOpen && {
        containerProps: { "data-focus": true },
    }));
};

export { convertDateToInput, convertDateToIso, useDatePickerRangeInput };
//# sourceMappingURL=useDatePickerRangeInput.js.map
